import { t } from '../../../lib/i18n';
import { invoiceNumberLabel } from '../labels';
import type { FinanceReceivable } from '../types';
import {
  RECEIVABLE_DOT,
  RECEIVABLE_TEXT,
  daysUntil,
  formatDayMonth,
  formatChf,
  receivableTone,
} from '../utils';
import { PanelEmpty } from './panel-state';

/**
 * «Найближчі надходження» — the next payments, in the order they land.
 *
 * Deliberately NOT the same data as the balances table: that table is per
 * client, this list is per invoice and sorted by date, so one client can appear
 * twice. An operator chasing money works down a calendar, not down a roster.
 *
 * ── Derived, because there is no `/receivables` endpoint ────────────────────
 * `FinanceReceivableSchema` exists in contracts but nothing on the API serves
 * it. The rows come from the client rollup instead (`upcomingReceivables` in
 * utils.ts), which already carries every unpaid invoice with its `dueAt` and
 * its client — so this list is a VIEW of the table above it rather than a
 * second source that could disagree with it.
 *
 * ── `tone` and the date phrase are derived at render ────────────────────────
 * Neither is on the wire, and that is the contract's decision: both are pure
 * functions of `dueAt` against now, and a server-computed "in 3 days" is stale
 * the moment the response is cached — it would still say "in 3 days" a week
 * later from a warm cache.
 */
export function UpcomingReceivables({ items }: { items: FinanceReceivable[] }) {
  return (
    <section className="flex flex-col rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-1.5 lg:gap-1 lg:px-[22px] lg:py-5">
      <h2 className="mt-2.5 mb-1 text-[16px] font-semibold lg:mt-0 lg:mb-2 lg:text-[18px] lg:tracking-[-0.02em]">
        {t('adminFinance.receivables.title')}
      </h2>

      {items.length === 0 ? (
        <PanelEmpty messageKey="adminFinance.receivables.empty" />
      ) : (
        items.map((item) => {
          const tone = receivableTone(item.dueAt);
          const days = daysUntil(item.dueAt);
          const when =
            days < 0
              ? t('adminFinance.receivables.late', {
                  days: Math.abs(days),
                  date: formatDayMonth(item.dueAt),
                })
              : t('adminFinance.receivables.due', { date: formatDayMonth(item.dueAt) });
          return (
            <div
              key={`${item.userId}-${item.invoiceNo}`}
              className="flex items-start gap-2.5 border-t border-cf-line-soft py-2.5 lg:gap-3"
            >
              <span
                className={`mt-[5px] h-2 w-2 shrink-0 rounded-cf-xs ${RECEIVABLE_DOT[tone]}`}
                aria-hidden
              />
              <div className="min-w-0 flex-1 text-[13px] leading-[1.4]">
                <div className="truncate">{item.clientName}</div>
                <div className={`font-mono text-[11px] ${RECEIVABLE_TEXT[tone]}`}>
                  {invoiceNumberLabel(item.invoiceNo)} · {when}
                </div>
              </div>
              <span className="font-mono font-medium whitespace-nowrap">
                {formatChf(item.amountChf)}
              </span>
            </div>
          );
        })
      )}
    </section>
  );
}
